import { useContext, useState } from 'react';

import { gql, useMutation, useQuery } from '@apollo/client';

import { PermissionKey } from 'auth/types';
import { LocalizationContext } from 'localization';
import notification from 'services/notificationService';
import { TGameAccount, TGameAccountsGroup, ELauncher } from 'types';

import {
  IAccountInput,
  ICreateAccountRequest,
  ICreateAccountResponse,
  ICreateAccountsGroupRequest,
  ICreateAccountsGroupResponse,
  IDeleteAccountRequest,
  IDeleteAccountResponse,
  IDeleteAccountsGroupRequest,
  IDeleteAccountsGroupResponse,
  IFreeAccountRequest,
  IFreeAccountResponse,
  IGameAccountsGroupsResponse,
  IReturnGameAccountsType,
  IUpdateAccountRequest,
  IUpdateAccountResponse,
  IUpdateAccountsGroupRequest,
  IUpdateAccountsGroupResponse,
} from './interfaces';
import {
  CREATE_ACCOUNT,
  CREATE_ACCOUNTS_GROUP,
  DELETE_ACCOUNT,
  DELETE_ACCOUNTS_GROUP,
  FREE_ACCOUNT,
  GAME_ACCOUNTS_GROUPS,
  UPDATE_ACCOUNT,
  UPDATE_ACCOUNTS_GROUP,
} from './schema';

export const useGameAccounts = (
  permissions: Set<PermissionKey> | null
): IReturnGameAccountsType => {
  const { t } = useContext(LocalizationContext);

  const { loading, error, data } = useQuery<IGameAccountsGroupsResponse>(
    GAME_ACCOUNTS_GROUPS,
    {
      fetchPolicy: 'cache-and-network',
      onError: console.error,
      skip: !permissions?.has('game_account_browse'),
    }
  );

  const [accountsGroupForEdit, setAccountsGroupForEdit] =
    useState<TGameAccountsGroup | null>(null);

  const [createAccountsGroupMutation] = useMutation<
    ICreateAccountsGroupResponse,
    ICreateAccountsGroupRequest
  >(CREATE_ACCOUNTS_GROUP, {
    update: (cache, { data }) => {
      const group = data?.createGameAccountGroup;

      if (!group) return;

      cache.modify({
        fields: {
          gameAccountGroups: (existingGroups = []) => {
            const newGroup = cache.writeFragment({
              id: cache.identify(group),
              data: group,
              fragment: gql`
                fragment group on GameAccountGroup {
                  id
                  title
                  launcher
                  accounts
                  shortcuts
                }
              `,
            });

            return [...existingGroups, newGroup];
          },
        },
      });
    },
    onError: console.error,
    onCompleted: () => {
      notification.info({
        t,
        title: t('gameAccounts.group.created'),
        type: 'success',
        dismiss: 5,
      });
    },
  });
  const createAccountsGroup = (
    title: string,
    launcher: ELauncher
  ): Promise<unknown> =>
    createAccountsGroupMutation({ variables: { input: { title, launcher } } });

  const [updateAccountsGroupMutation] = useMutation<
    IUpdateAccountsGroupResponse,
    IUpdateAccountsGroupRequest
  >(UPDATE_ACCOUNTS_GROUP, {
    update: (cache, { data }) => {
      const group = data?.updateGameAccountGroup;

      if (!group) return;

      cache.writeFragment({
        id: cache.identify(group),
        data: group,
        fragment: gql`
          fragment group on GameAccountGroup {
            title
            launcher
          }
        `,
      });
    },
    onError: console.error,
    onCompleted: () => {
      notification.info({
        t,
        title: t('gameAccounts.group.updated'),
        type: 'success',
        dismiss: 5,
      });
    },
  });
  const updateAccountsGroup = (
    id: number,
    title: string,
    launcher: ELauncher
  ): Promise<unknown> =>
    updateAccountsGroupMutation({
      variables: { id, input: { title, launcher } },
    });

  const [deleteAccountsGroupMutation] = useMutation<
    IDeleteAccountsGroupResponse,
    IDeleteAccountsGroupRequest
  >(DELETE_ACCOUNTS_GROUP, {
    update: (cache, { data }) => {
      const group = data?.deleteGameAccountGroup;

      if (!group) return;

      cache.evict({
        id: cache.identify(group),
      });
      cache.gc();
    },
    onError: console.error,
    onCompleted: () => {
      notification.info({
        t,
        title: t('gameAccounts.group.deleted'),
        type: 'success',
        dismiss: 5,
      });
    },
  });
  const deleteAccountsGroup = (id: number): Promise<unknown> =>
    deleteAccountsGroupMutation({ variables: { id } });

  const [accountForEdit, setAccountForEdit] = useState<TGameAccount | null>(
    null
  );

  const [createAccountMutation] = useMutation<
    ICreateAccountResponse,
    ICreateAccountRequest
  >(CREATE_ACCOUNT, {
    update: (cache, { data }) => {
      const account = data?.createGameAccount;

      if (!account) return;

      cache.modify({
        id: cache.identify(account.group),
        fields: {
          accounts: (accounts) => {
            const newAccount = cache.writeFragment({
              id: cache.identify(account),
              data: account,
              fragment: gql`
                fragment account on GameAccount {
                  id
                  login
                  password
                  active
                  status
                  busy
                  assignment {
                    host {
                      id
                      alias
                      group_id
                    }
                  }
                  group {
                    id
                    title
                  }
                  host {
                    id
                    alias
                  }
                }
              `,
            });

            return [...accounts, newAccount];
          },
        },
      });
    },
    onError: console.error,
    onCompleted: () => {
      notification.info({
        t,
        title: t('gameAccounts.added'),
        type: 'success',
        dismiss: 5,
      });
    },
  });
  const createAccount = (input: IAccountInput): Promise<unknown> =>
    createAccountMutation({ variables: { input } });

  const [updateAccountMutation] = useMutation<
    IUpdateAccountResponse,
    IUpdateAccountRequest
  >(UPDATE_ACCOUNT, {
    update: (cache, { data }) => {
      const account = data?.updateGameAccount;

      if (!account) return;

      cache.writeFragment({
        id: cache.identify(account),
        data: account,
        fragment: gql`
          fragment group on GameAccount {
            login
            password
            active
            assignment {
              host {
                id
                alias
                group_id
              }
            }
          }
        `,
      });
    },
    onError: console.error,
    onCompleted: () => {
      notification.info({
        t,
        title: t('gameAccounts.updated'),
        type: 'success',
        dismiss: 5,
      });
    },
  });
  const updateAccount = (id: number, input: IAccountInput): Promise<unknown> =>
    updateAccountMutation({ variables: { id, input } });

  const [deleteAccountMutation] = useMutation<
    IDeleteAccountResponse,
    IDeleteAccountRequest
  >(DELETE_ACCOUNT, {
    update: (cache, { data }) => {
      const account = data?.deleteGameAccount;

      if (!account) return;

      cache.evict({
        id: cache.identify(account),
      });
      cache.gc();
    },
    onError: console.error,
    onCompleted: () => {
      notification.info({
        t,
        title: t('gameAccounts.deleted'),
        type: 'success',
        dismiss: 5,
      });
    },
  });
  const deleteAccount = (id: number): Promise<unknown> =>
    deleteAccountMutation({ variables: { id } });

  const [freeGameAccountMutation] = useMutation<
    IFreeAccountResponse,
    IFreeAccountRequest
  >(FREE_ACCOUNT, {
    awaitRefetchQueries: true,
    refetchQueries: [{ query: GAME_ACCOUNTS_GROUPS }],
    update: (cache, { data }) => {
      const account = data?.freeGameAccount;

      if (!account) return;

      cache.writeFragment({
        id: cache.identify(account),
        data: account,
        fragment: gql`
          fragment account on GameAccount {
            busy
          }
        `,
      });
    },
    onError: console.error,
    onCompleted: () => {
      notification.info({
        t,
        title: t('gameAccounts.updated.free'),
        type: 'success',
        dismiss: 5,
      });
    },
  });
  const freeAccount = (id: number): Promise<unknown> =>
    freeGameAccountMutation({ variables: { id } });

  return {
    accountsGroups: data?.gameAccountGroups ?? [],
    error,
    loading,
    accountsGroupForEdit,
    setAccountsGroupForEdit,
    accountForEdit,
    setAccountForEdit,
    createAccountsGroup,
    updateAccountsGroup,
    deleteAccountsGroup,
    createAccount,
    updateAccount,
    deleteAccount,
    freeAccount,
  };
};
